import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import API from "../../common/API"

interface ActivityLogItem {
    id: string
    date?: string
    userId?: string
    userName?: string
    userImage?: string
    section?: string
    action?: string
    itemId?: string
    content?: string
    ipAddress?: string
}

const PAGE_SIZE = 20

const AdminActivityLogComponent = () => {
    const { t } = useTranslation("global")
    const [records, setRecords] = useState<ActivityLogItem[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [query, setQuery] = useState("")
    const [section, setSection] = useState("")
    const [page, setPage] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [totalRecords, setTotalRecords] = useState(0)

    const bindData = useCallback(async () => {
        try {
            setIsLoading(true)
            const params = new URLSearchParams()
            params.set("Page", String(page))
            params.set("Quantity", String(PAGE_SIZE))
            if (query.trim()) params.set("Query", query.trim())
            if (section.trim()) params.set("Section", section.trim())

            const res = await API.getAction(`ActivityLog/List?${params.toString()}`)
            if (res.data.status === "ok") {
                setRecords(res.data.response || [])
                setTotalPages(res.data.quantityOfPages || 0)
                setTotalRecords(res.data.quantityOfRecords || 0)
            }
        } catch (error) {
            console.error(error)
            setRecords([])
            setTotalPages(0)
            setTotalRecords(0)
        } finally {
            setIsLoading(false)
        }
    }, [page, query, section])

    useEffect(() => {
        bindData()
    }, [bindData])

    const handleSearch = () => {
        setPage(0)
        bindData()
    }

    return (
        <div>
            <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                <h5 className="fw-bold mb-0">
                    <i className="fas fa-history me-2 text-primary" />
                    {t("Activity Log")}
                </h5>
                <span className="badge bg-light text-dark">{totalRecords.toLocaleString()} registros</span>
            </div>

            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-12 col-md-5">
                            <label className="form-label small text-muted">Buscar</label>
                            <input
                                type="text"
                                className="form-control"
                                value={query}
                                placeholder="usuario, accion, modulo, IP..."
                                onChange={e => setQuery(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") handleSearch()
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-4">
                            <label className="form-label small text-muted">Modulo (section)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={section}
                                placeholder="Dashboard, Users, Projects..."
                                onChange={e => setSection(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") handleSearch()
                                }}
                            />
                        </div>
                        <div className="col-12 col-md-3 d-flex gap-2">
                            <button className="btn btn-primary w-100" onClick={handleSearch}>
                                <i className="fas fa-search me-2" />
                                Buscar
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                    setQuery("")
                                    setSection("")
                                    setPage(0)
                                }}
                                title="Limpiar"
                            >
                                <i className="fas fa-eraser" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {isLoading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary" role="status" />
                        </div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-5 text-muted">No hay registros para los filtros actuales.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-striped table-hover align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Usuario</th>
                                        <th>Módulo</th>
                                        <th>Acción</th>
                                        <th>Contenido</th>
                                        <th>IP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map(item => (
                                        <tr key={item.id}>
                                            <td>
                                                {item.date ? new Date(item.date).toLocaleString() : "-"}
                                            </td>
                                            <td>{item.userName || item.userId || "-"}</td>
                                            <td>{item.section || "-"}</td>
                                            <td>{item.action || "-"}</td>
                                            <td>
                                                <span className="text-muted">{item.content || item.itemId || "-"}</span>
                                            </td>
                                            <td>{item.ipAddress || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="card-footer d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        Página {totalPages === 0 ? 0 : page + 1} de {totalPages}
                    </small>
                    <div className="btn-group">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={page <= 0}
                            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                        >
                            <i className="fas fa-chevron-left me-1" />
                            Anterior
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            disabled={page >= totalPages - 1 || totalPages === 0}
                            onClick={() => setPage(prev => prev + 1)}
                        >
                            Siguiente
                            <i className="fas fa-chevron-right ms-1" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminActivityLogComponent
